import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import clsx from "clsx";

import type { ServerDataSuccess } from "./route";

import {
  Button,
  Sheet,
  ShopifyRichText,
  IconMeasuringTape,
  type TableProps,
  Table,
  IconVintageArrow,
} from "@bared/ui";
import { Sections } from "~/components/Sections/Sections";

import { useLiveChat } from "~/hooks/useLiveChat";
import { useRichText } from "@hooks/useRichText";

export interface SizeGuideProps {
  sizeGuide: ServerDataSuccess["sizeGuideSheetContents"];
  gender: "Mens" | "Womens";
  sheetFooterContent: ServerDataSuccess["template"]["sheetFooterContent"];
}

export function SizeGuideSheet(props: SizeGuideProps) {
  const liveChat = useLiveChat();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleSheetToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  useEffect(() => {
    const mainContainer = document.getElementById("content-main");

    if (!mainContainer) return;

    if (isOpen) {
      liveChat.hide();
      mainContainer.style.zIndex = "50";
    } else {
      liveChat.show();
      mainContainer.style.removeProperty("z-index");
    }
  }, [isOpen, liveChat]);

  return (
    <>
      <div className="mb-mini">
        <Button
          variant="link"
          onClick={handleSheetToggle}
          className="flex items-baseline"
        >
          <IconMeasuringTape className="w-mini text-primary hover:text-secondary" />
          Size Guide
        </Button>
      </div>

      <Sheet
        isOpen={isOpen}
        onClose={handleSheetToggle}
        title="Size Guide"
        className="p-midi md:p-maxi"
      >
        <SheetContent {...props} handleSheetToggle={handleSheetToggle} />
      </Sheet>
    </>
  );
}

function SheetContent({
  sizeGuide,
  gender,
  sheetFooterContent,
  handleSheetToggle,
}: SizeGuideProps & {
  handleSheetToggle: () => void;
}) {
  const { parseContent } = useRichText();
  const footerContent = parseContent(sheetFooterContent);

  if (!sizeGuide) return null;

  const sheetContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const {
    men,
    women,
    summaryMens,
    summaryWomens,
    flexibleSections,
    sizingAdvice,
  } = sizeGuide;

  const sheetContent = useMemo(() => {
    if (gender === "Mens") {
      return {
        summary: summaryMens,
        sizes: men,
        flexibleSections,
        sizingAdvice,
      };
    }

    if (gender === "Womens") {
      return {
        summary: summaryWomens,
        sizes: women,
        flexibleSections,
        sizingAdvice,
      };
    }
  }, [gender]);
  const summary = parseContent(sheetContent?.summary);

  const tableHeaders: TableProps["columns"] = [
    { name: "Bared/EU", id: "bared", isRowHeader: true },
    { name: "US", id: "us_au" },
    { name: "CM", id: "cm" },
    { name: "IN", id: "inch" },
  ];

  const tableCells: TableProps["row"] = sheetContent?.sizes
    ? sheetContent.sizes.map((size, index) => ({
        id: index + 1,
        bared: size.bared,
        us_au: size.us_au,
        cm: size.cm,
        inch: size.inch,
      }))
    : [];

  // Function to update the scroll state of table (whether it's possible to scroll left or right)
  const updateTableScrollState = () => {
    if (tableRef.current) {
      const scrollLeft = Math.round(tableRef.current.scrollLeft);
      const scrollWidth = tableRef.current.scrollWidth;
      const clientWidth = tableRef.current.clientWidth;

      // Can scroll left if scrollLeft > 0
      setCanScrollLeft(scrollLeft > 0);

      // Can scroll right if the total scrollable width exceeds the visible width
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const handleScrollRight = () => {
    if (tableRef.current && canScrollRight) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft + 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  const handleScrollLeft = () => {
    if (tableRef.current && canScrollLeft) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft - 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      updateTableScrollState();

      // Add scroll event listener to tableRef to update the scroll state
      tableRef.current.addEventListener("scroll", updateTableScrollState);
    }

    window.addEventListener("resize", updateTableScrollState);

    return () => {
      tableRef.current?.removeEventListener("scroll", updateTableScrollState);
      window.removeEventListener("resize", updateTableScrollState);
    };
  }, [tableRef.current, updateTableScrollState]);

  useEffect(() => {
    const links = sheetContainerRef.current?.querySelectorAll("a");

    if (!links) return;

    // Add a click event listener to all links in the sheet container to determine if the sheet should be closed or not
    links.forEach((link) => {
      if (link.target === "_blank") return;

      link.addEventListener("click", () => {
        handleSheetToggle();
      });
    });
  }, [handleSheetToggle]);

  return (
    <div ref={sheetContainerRef}>
      <h3 className="font-display text-h3 leading-h3">Size Guide</h3>

      <div className="flex flex-col space-y-micro">
        {!!sheetContent?.sizingAdvice && (
          <ShopifyRichText content={sheetContent.sizingAdvice} />
        )}
        {!!summary && summary}
      </div>

      <div className="flex flex-col space-y-midi py-midi">
        {tableCells.length > 0 && (
          <div className="relative">
            <TableControls
              scrollRight={handleScrollRight}
              scrollLeft={handleScrollLeft}
              canScrollRight={canScrollRight}
              canScrollLeft={canScrollLeft}
            />
            <Table
              tableHeaderClassName="p-micro text-caption bg-primary-100 font-normal"
              cellClassName="p-micro text-caption"
              columns={tableHeaders}
              row={tableCells}
              orientation="horizontal"
              tableRef={tableRef}
            />
          </div>
        )}

        {!!sheetContent?.flexibleSections && (
          <div className="grid grid-cols-1 gap-micro md:grid-cols-2 md:gap-mini">
            <Sections page={{ content: sheetContent?.flexibleSections }} />
          </div>
        )}
      </div>

      {!!footerContent && (
        <div className="border-t border-primary-300 pt-midi">
          {footerContent}
        </div>
      )}
    </div>
  );
}

interface TableControlsProps {
  scrollRight: () => void;
  scrollLeft: () => void;
  canScrollRight: boolean;
  canScrollLeft: boolean;
}

function TableControls({
  scrollRight,
  scrollLeft,
  canScrollRight,
  canScrollLeft,
}: TableControlsProps) {
  return (
    <div className="mb-micro flex justify-end space-x-micro">
      <button
        disabled={!canScrollLeft}
        onClick={() => scrollLeft()}
        aria-label="Previous"
        aria-disabled={!canScrollLeft}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollLeft && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="left" />
      </button>
      <button
        aria-label="Next"
        disabled={!canScrollRight}
        onClick={() => scrollRight()}
        aria-disabled={!canScrollRight}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollRight && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="right" />
      </button>
    </div>
  );
}
